import { dateOnlyOf, parseIsoDate, toDateOnlyValue } from '@/lib/utils/custom-date/custom-date';
import * as z from 'zod';
import { Employee, EmployeeSkill, EmployeeSubCategoryExperience } from '../types/employee.types';

// --- Skills an employee records about themselves (self-service profile edit) -------------------------
// A skill is a `Skill` of the Skill Manager, which belongs to one skill sub-category. The experience is recorded per sub-category, and a
// person can pick the skills of the sub-categories they recorded. Both are lists embedded in the
// UserProfileDetail (`Skills` of `UserSkill`, `SubCategoryExperiences` of `UserSubCategoryExperience`),
// like the legacy lists. The person's skill category (Business, Tech, ...) is one field of the profile
// (`CategoryId`), independent of the sub-categories.
// Entries saved by the legacy app hold a skill key or name; they are matched to a skill by key or name,
// and kept as they are ("old") when nothing matches.

export const PROFICIENCY_LEVELS = [
  { key: 1, title: 'Basic' },
  { key: 2, title: 'Advanced' },
  { key: 3, title: 'Professional' },
] as const;

const requiredMessage = { message: 'ERR_MSG_REQUIRED' };

export const subCategoryExperienceSchema = z
  .object({
    /** Id of the SkillSubCategory. */
    SubCategoryId: z.string().min(1, requiredMessage),
    StartDate: z.string().min(1, requiredMessage),
    EndDate: z.string(),
    IsUntilNow: z.boolean(),
  })
  .superRefine((value, ctx) => {
    if (value.IsUntilNow) return;
    if (!value.EndDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['EndDate'],
        message: 'ERR_MSG_REQUIRED',
      });
    } else if (value.StartDate && value.EndDate < value.StartDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['EndDate'],
        message: 'START_DATE_MUST_NOT_BE_AFTER_END_DATE',
      });
    }
  });

const skillEntrySchema = z.object({
  /** Id of the Skill (an old entry: the stored key or name, see `resolveStoredSkill`). */
  Skill: z.string().min(1, requiredMessage),
  /** Name to show while `Skill` is no current skill (an old entry). */
  SkillName: z.string(),
  Proficiency: z.number().int().min(1, requiredMessage).max(PROFICIENCY_LEVELS.length),
});

export const skillsShape = {
  /** Id of the SkillCategory. */
  CategoryId: z.string().min(1, requiredMessage),
  SubCategoryExperiences: z.array(subCategoryExperienceSchema),
  Skills: z.array(skillEntrySchema),
};

export type SubCategoryExperienceValues = z.infer<typeof subCategoryExperienceSchema>;
export type SkillEntryValues = z.infer<typeof skillEntrySchema>;
export type SkillsValues = {
  CategoryId: string;
  SubCategoryExperiences: SubCategoryExperienceValues[];
  Skills: SkillEntryValues[];
};

export const createEmptySubCategoryExperience = (): SubCategoryExperienceValues => ({
  SubCategoryId: '',
  StartDate: '',
  EndDate: '',
  IsUntilNow: false,
});

export const createEmptySkillEntry = (): SkillEntryValues => ({
  Skill: '',
  SkillName: '',
  Proficiency: 1,
});

/** Sub-category titles of the legacy numeric `SubCategory` enum, for records that were never re-saved. */
const LEGACY_SUB_CATEGORY_TITLES = [
  'Business',
  'FrontEnd',
  'BackEnd',
  'FullStack',
  'QA',
  'DevOps',
  'UX',
  'UI',
  'Marketing',
  'HR',
  'Finance',
  'AI',
];

const normalize = (value: string) => value.trim().toLowerCase();

/**
 * The SkillSubCategory a stored value points at: its id, or - for records saved by the legacy app - its
 * title or its numeric enum key. Returns the stored value itself when nothing matches.
 */
export const resolveSubCategoryId = (
  value: string | number,
  subCategories: { ItemId: string; Title: string }[]
) => {
  const stored = String(value);
  const byId = subCategories.find((subCategory) => subCategory.ItemId === stored);
  if (byId) return byId.ItemId;
  const legacyTitle = /^\d+$/.test(stored) ? LEGACY_SUB_CATEGORY_TITLES[Number(stored)] : undefined;
  const title = normalize(legacyTitle ?? stored);
  return (
    subCategories.find((subCategory) => normalize(subCategory.Title) === title)?.ItemId ?? stored
  );
};

/** A skill of the Skill Manager. */
export interface SkillLookup {
  ItemId: string;
  Name: string;
  SubCategoryId: string;
  /** Key of the skill in the legacy app, which saved a person's skill as it (e.g. `csharp`). */
  LegacyKey?: string | null;
}

/**
 * The name a stored entry was saved under: its `SkillName`, else the stored value itself (legacy entries
 * hold a name).
 */
export const storedSkillName = (entry: { Skill: string; SkillName?: string | null }) =>
  entry.SkillName || entry.Skill;

/**
 * The Skill a stored entry points at: the skill with its id, else - among the skills of the given
 * sub-categories - the one whose legacy key is the stored value (the legacy app saved `csharp` for "C#"),
 * else the one with its name (case-insensitive), else nothing - an old entry kept as it is.
 */
export const resolveStoredSkill = (
  entry: { Skill: string; SkillName?: string | null },
  skills: SkillLookup[],
  subCategoryIds: string[]
): SkillLookup | undefined => {
  const byId = skills.find((skill) => skill.ItemId === entry.Skill);
  if (byId) return byId;
  const candidates = skills.filter((skill) => subCategoryIds.includes(skill.SubCategoryId));
  const key = normalize(entry.Skill);
  const byKey = candidates.find((skill) => skill.LegacyKey && normalize(skill.LegacyKey) === key);
  if (byKey) return byKey;
  const name = normalize(storedSkillName(entry));
  return candidates.find((skill) => normalize(skill.Name) === name);
};

export const toSubCategoryExperienceValues = (
  stored?: EmployeeSubCategoryExperience[] | null
): SubCategoryExperienceValues[] =>
  (stored ?? []).map((entry) => {
    // Records saved before `SubCategoryId` existed hold the id (or a legacy title / key) in `SubCategory`.
    const subCategory = entry.SubCategoryId || entry.SubCategory;
    return {
      SubCategoryId: subCategory === null || subCategory === undefined ? '' : String(subCategory),
      StartDate: dateOnlyOf(entry.StartDate),
      EndDate: dateOnlyOf(entry.EndDate),
      IsUntilNow: Boolean(entry.IsUntilNow),
    };
  });

export const toSkillValues = (stored?: EmployeeSkill[] | null): SkillEntryValues[] =>
  (stored ?? [])
    .filter((entry) => entry.Skill)
    .map((entry) => ({
      Skill: String(entry.Skill),
      SkillName: entry.SkillName ?? '',
      Proficiency: Number(entry.Proficiency) || 1,
    }));

/**
 * What to load (`getSkillsFor`) to show or edit a stored profile's skills: the stored skill values and
 * the profile's sub-categories as today's ids. The profile view and the edit page ask the same, so they
 * share one request.
 */
export const skillLookupOf = (
  employee: Employee,
  subCategories: { ItemId: string; Title: string }[]
) => ({
  skillIds: toSkillValues(employee.Skills).map((entry) => entry.Skill),
  subCategoryIds: toSubCategoryExperienceValues(employee.SubCategoryExperiences).map((entry) =>
    resolveSubCategoryId(entry.SubCategoryId, subCategories)
  ),
});

export const skillsFromEmployee = (employee: Employee): SkillsValues => ({
  CategoryId: employee.CategoryId ?? '',
  SubCategoryExperiences: toSubCategoryExperienceValues(employee.SubCategoryExperiences),
  Skills: toSkillValues(employee.Skills),
});

/** Whole months between two yyyy-MM-dd dates; a running experience ends today. */
export const experienceMonths = (
  start: string,
  end: string,
  isUntilNow: boolean,
  today = new Date()
): number => {
  // Calendar days, read as local dates like `today`; `new Date('yyyy-MM-dd')` would be UTC midnight.
  const from = parseIsoDate(start);
  if (!from) return 0;
  const to = (!isUntilNow && parseIsoDate(end)) || today;
  const months = (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth());
  return Math.max(0, to.getDate() < from.getDate() ? months - 1 : months);
};

/**
 * Legacy stored the experience length with each entry (a computed column in its DB). Each entry keeps
 * the sub-category's id and its title at the time of saving; each skill its name and sub-category (an
 * old entry that matches no skill keeps the name it was shown under, and no sub-category).
 */
export const skillsToPayload = (
  values: SkillsValues,
  subCategories: { ItemId: string; Title: string }[] = [],
  skills: SkillLookup[] = []
): {
  CategoryId: string;
  SubCategoryExperiences: EmployeeSubCategoryExperience[];
  Skills: EmployeeSkill[];
} => ({
  CategoryId: values.CategoryId,
  SubCategoryExperiences: values.SubCategoryExperiences.map((entry) => ({
    SubCategoryId: entry.SubCategoryId,
    SubCategory:
      subCategories.find((subCategory) => subCategory.ItemId === entry.SubCategoryId)?.Title ?? '',
    StartDate: toDateOnlyValue(entry.StartDate) ?? null,
    EndDate: entry.IsUntilNow ? null : (toDateOnlyValue(entry.EndDate) ?? null),
    IsUntilNow: entry.IsUntilNow,
    TotalExperienceMonths: experienceMonths(entry.StartDate, entry.EndDate, entry.IsUntilNow),
  })),
  Skills: values.Skills.filter((skill) => skill.Skill).map(({ Skill, SkillName, Proficiency }) => {
    const current = skills.find((skill) => skill.ItemId === Skill);
    return {
      Skill,
      SkillName: current?.Name ?? SkillName,
      SubCategoryId: current?.SubCategoryId ?? '',
      Proficiency,
    };
  }),
});

/** "2 years 3 months" pieces for display. */
export const formatExperience = (months: number): { years: number; months: number } => ({
  years: Math.floor(months / 12),
  months: months % 12,
});
